import React from "react";

import { Box, Container, Grid, IconButton, Link, Typography } from "@material-ui/core";
import { Close } from "@material-ui/icons";

import DesktopContainerDrawer from "containers/DesktopContainerDrawer";
import MobileContainerDrawer from "containers/MobileContainerDrawer";
import makeStyles from "hooks/makeStyles";
import useBusiness from "hooks/useBusiness";
import useTranslation from "hooks/useTranslation";
import AppLogoIcon from "icons/AppLogoIcon";
import ClockIcon from "icons/ClockIcon";
import FacebookIcon from "icons/FacebookIcon";
import InformationWhiteIcon from "icons/InformationWhiteIcon";
import InstagramIcon from "icons/InstagramIcon";
import PhoneIcon from "icons/PhoneIcon";
import TwitterIcon from "icons/TwitterIcon";
import WebLinkIcon from "icons/WebLinkIcon";
import YelpIcon from "icons/YelpIcon";

const useStyles = makeStyles(theme => ({
	root: {
		alignItems: "center",
		display: "flex",
		flex: 1,
		flexDirection: "column",
		padding: 0,
		height: "calc(45vh - 95px)", // not great..
		position: "relative",
		overflowY: "auto",
		overflowX: "hidden",
		paddingRight: theme.spacing(2),
		[theme.breakpoints.up(480)]: {
			overflow: "hidden",
			height: "100vh",
			paddingRight: 0
		}
	},
	body: {
		[theme.breakpoints.up(480)]: {
			width: "100%",
			backgroundColor: theme.palette.background.default,
			color: theme.palette.text.primary,
			borderBottomLeftRadius: theme.spacing(2),
			borderBottomRightRadius: theme.spacing(2),
			padding: theme.spacing(4)
		}
	},
	scroll: {
		display: "flex",
		flexDirection: "column",
		height: "calc(100vh - 195px)",
		position: "relative",
		paddingRight: 20, // improves scrollbar placement
		marginRight: 0, // improves scrollbar placement
		overflowX: "initial",
		overflowY: "scroll",
		paddingBottom: 40,
		width: "100%",
		[theme.breakpoints.up(480)]: {
			overflowY: "auto",
			height: "auto",
			maxHeight: "calc(50vh)"
		}
	},
	popupTitle: {
		height: "20px"
	},
	link: {
		color: theme.palette.secondary.light,
		[theme.breakpoints.up(480)]: {
			color: theme.palette.primary.dark
		}
	},

	closeButton: {
		marginLeft: -2,
		marginTop: -2,
		padding: 0,
		textAlign: "left",
		alignSelf: "flex-start",
		[theme.breakpoints.up(480)]: {
			alignSelf: "flex-end",
			position: "absolute",
			top: theme.spacing(2),
			right: theme.spacing(2)
		}
	},
	closeIcon: {
		width: 16,
		height: 16,
		color: "#fff",
		[theme.breakpoints.up(480)]: {
			fill: theme.palette.text.primary
		}
	},
	section: {
		display: "flex",
		flexDirection: "column",
		alignItems: "left"
	},
	socialSection: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between"
	},
	twoColSection: {
		display: "flex",
		flexDirection: "row",
		alignItems: "left",
		paddingRight: theme.spacing(2)
	},
	sectionTitle: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		marginTop: "8px",
		marginBottom: "8px",
		"&:hover": {
			textDecoration: "none"
		}
	},
	logo: {
		height: 64
	},
	user: {},
	logoIcon: {
		marginRight: theme.spacing(1)
	},
	fab: {
		position: "relative",
		height: "64px"
	},
	businessName: {
		textTransform: "uppercase",
		marginBottom: theme.spacing(2)
	},
	sectionTitleText: {
		textAlign: "left",
		textTransform: "uppercase",
		textDecoration: "none",
		color: "#fff",
		[theme.breakpoints.up(480)]: {
			color: theme.palette.text.primary
		}
	},
	left: {
		width: "100%",
		textTransform: "uppercase"
	},
	right: {
		whiteSpace: "nowrap"
	},
	socialTitle: {
		textTransform: "uppercase"
	},
	icon: {
		marginTop: "3px",
		marginRight: theme.spacing(1),
		fill: "#fff",
		[theme.breakpoints.up(480)]: {
			fill: theme.palette.text.primary
		}
	},
	socialIcon: {
		marginRight: theme.spacing(1),
		fill: "#fff",
		height: theme.spacing(2),
		[theme.breakpoints.up(480)]: {
			fill: theme.palette.text.primary
		}
	},
	iconLink: {},
	phoneIcon: {
		height: "12px"
	},
	text: {
		fontSize: 12,
		whiteSpace: "nowrap"
	},
	workingHours: {
		textTransform: "uppercase"
	},
	footer: {
		[theme.breakpoints.up(480)]: {
			marginTop: 0,
			width: "auto",
			bottom: "6px",
			flex: "1",
			justifyContent: "center",
			paddingLeft: theme.spacing(6),
			paddingTop: theme.spacing(3),
			paddingRight: theme.spacing(3)
		}
	}
}));
interface SocialLinkProps {
	provider: string;
	className: string;
}

interface Props {
	onClose: () => void;
}

const SocialLink = ({ provider, className }: SocialLinkProps) => (
	<>
		{provider === "instagram" && <InstagramIcon className={className} />}
		{provider === "twitter" && <TwitterIcon className={className} />}
		{provider === "yelp" && <YelpIcon className={className} />}
		{provider === "facebook" && <FacebookIcon className={className} />}
	</>
);

const CompanyInfoModal = ({ onClose }: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const { business } = useBusiness();

	const {
		displayBusinessHours,
		description,
		displayPhoneNumber,
		websiteUrl,
		phoneNumber,
		name,
		logoUrl,
		businessLinks
	} = business;
	const webLinks = websiteUrl || (businessLinks && businessLinks.length > 0);
	return (
		<Container className={classes.root}>
			<MobileContainerDrawer>
				<IconButton className={classes.closeButton} onClick={onClose}>
					<Close className={classes.closeIcon} />
				</IconButton>
			</MobileContainerDrawer>
			<DesktopContainerDrawer>
				<Box className={classes.body}>
					<IconButton className={classes.closeButton} onClick={onClose}>
						<Close className={classes.closeIcon} />
					</IconButton>
					<Box className={classes.scroll}>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<Box className={classes.logo}>
									<Box className={classes.fab}>
										{logoUrl ? (
											<img src={logoUrl} className={classes.logo} />
										) : (
											<AppLogoIcon className={classes.logoIcon} />
										)}
									</Box>
								</Box>
								<Box className={classes.sectionTitle}>
									<Typography variant="h2" className={classes.businessName}>
										{name}
									</Typography>
								</Box>
								{description && (
									<Box className={classes.section}>
										<Typography className={classes.workingHours}>{description}</Typography>
									</Box>
								)}
							</Grid>
						</Grid>
					</Box>
				</Box>
			</DesktopContainerDrawer>
			<Box className={classes.footer}>
				<Grid container spacing={3}>
					{phoneNumber && (
						<Grid item xs={12}>
							<Link href={`tel:${phoneNumber}`} className={classes.sectionTitle}>
								<PhoneIcon className={`${classes.icon} ${classes.phoneIcon}`} />
								<Typography variant="subtitle2" className={classes.sectionTitleText}>
									{t("phoneNumber")}
								</Typography>
							</Link>
							<Box className={classes.twoColSection} key={"phone-number"}>
								<Typography className={classes.left}>{t("phone")}</Typography>
								<Typography className={classes.right}>
									<Link href={`tel:${phoneNumber}`} className={classes.link}>
										{displayPhoneNumber}
									</Link>
								</Typography>
							</Box>
						</Grid>
					)}
					{webLinks && (
						<Grid item xs={12}>
							<Box className={classes.sectionTitle}>
								<WebLinkIcon className={classes.icon} />
								<Typography variant="subtitle2" className={classes.sectionTitleText}>
									{t("webLinks")}
								</Typography>
							</Box>
							{websiteUrl && (
								<Box className={classes.section} key={"restaurant-home-page"}>
									<Typography className={classes.left}>{t("restaurantHomePage")}</Typography>
									<Typography className={classes.workingHours}>
										<Link href={websiteUrl} className={classes.link} target="_blank">
											{websiteUrl}
										</Link>
									</Typography>
								</Box>
							)}
							{businessLinks && businessLinks.length > 0 && (
								<Box className={classes.socialSection} key={"restaurant-socials"}>
									<Typography className={classes.socialTitle}>{t("socials")}</Typography>
									<Typography className={classes.workingHours}>
										{businessLinks.map(link => (
											<Link
												key={`sociallink-${link.provider}`}
												href={link.url}
												className={classes.iconLink}
												target="_blank"
											>
												<SocialLink
													provider={link.provider}
													className={classes.socialIcon}
													key={`social-${link.provider}`}
												/>
											</Link>
										))}
									</Typography>
								</Box>
							)}
						</Grid>
					)}
					{displayBusinessHours && displayBusinessHours?.length > 0 && (
						<Grid item xs={12}>
							<Box className={classes.sectionTitle}>
								<ClockIcon className={classes.icon} />
								<Typography variant="subtitle2" className={classes.sectionTitleText}>
									{t("hours")}
								</Typography>
							</Box>
							{business.displayBusinessHours?.map((businessHour, i) => (
								<Box className={classes.section} key={i}>
									<Typography className={classes.workingHours}>{businessHour}</Typography>
								</Box>
							))}
						</Grid>
					)}
					{description && (
						<MobileContainerDrawer>
							<Grid item xs={12}>
								<Box className={classes.sectionTitle}>
									<InformationWhiteIcon className={classes.icon} />
									<Typography variant="subtitle2" className={classes.sectionTitleText}>
										{t("description")}
									</Typography>
								</Box>
								<Box className={classes.section}>
									<Typography className={classes.workingHours}>{description}</Typography>
								</Box>
							</Grid>
						</MobileContainerDrawer>
					)}
				</Grid>
			</Box>
		</Container>
	);
};

export default CompanyInfoModal;
